// eslint-disable-next-line no-restricted-imports
import { t } from "@comact/crc";
import { INodeHierarchical as INodeHierarchicalTree } from "@comact/crc/modules/kit/Tree";
import _ from "lodash";
import { memoize } from "proxy-memoize";
import { createSelector } from "reselect";
import { NodesModule } from ".";
import { ITemplate } from "../nodeTemplate";
import { IPermissionLevel, UserModule } from "../users";
import {
    extractLocationFromNodeId,
    extractMillFromNodeId,
    extractOrganizationFromNodeId,
    getConnectionStateForNode,
    IBasicNode,
    IBasicNodes,
    IBranchStatuses,
    IConnectionStatusState,
    IMillNode,
    INode,
    INodeHierarchical,
    INodeInfoAndHierarchy,
    INodesInfoAndHierarchy,
    INodeStateStyle
} from "./model";

export const getDetailedNodeById = <T extends INode>(state: IStoreState, id: string): T => state.detailedNodes?.[id] as T;

export const getNodeDisplayName = (state: IStoreState, nodeId: string): string => (
    state.aliases?.[nodeId] || getDetailedNodeById(state, nodeId)?.name || getBasicNodeById(state, nodeId)?.name || nodeId
);

export const getNodeMetadataById = (state: IStoreState, id: string): INodeInfoAndHierarchy => getNodesHierarchy(state)?.[id];

export const getListOfBasicNodesById = memoize(({ state, ids }: { state: IStoreState; ids: string[]; }): IBasicNode[] => _.map(ids, (id) => getBasicNodeById(state, id)));

export const getListOfNodesInfoAndHierarchyById = memoize(({ state, ids }: { state: IStoreState; ids: string[]; }): INodeInfoAndHierarchy[] => {
    const nodes = getNodesHierarchy(state);
    return _.map(ids, (id) => nodes[id]);
});

export const getNodeConnectionState = (state: IStoreState, id: string): IConnectionStatusState => getConnectionStateForNode(getBasicNodeById(state, id));

export const getNodeNamePath = ((state: IStoreState, nodeId: string, includeMill?: boolean) => {
    const hierarchy = getNodesHierarchy(state);
    const node = hierarchy[nodeId];
    if (!node) return null;
    let nodeNamePath = getNodeDisplayName(state, nodeId);
    let newParent = node;
    while (newParent) { // We want to build the node name path up to the mill
        newParent = hierarchy[newParent.parentId];
        if (!newParent || (newParent.templateName == "millCodec" && !includeMill)) break;
        nodeNamePath = `${getNodeDisplayName(state, newParent.id)} - ${nodeNamePath}`;
        if (newParent.templateName == "millCodec" && includeMill) break;
    }
    return nodeNamePath;
});

export const getNodeTemplateNameLocalized = (state: IStoreState, nodeId: string) => t(`node.template.${getBasicNodeById(state, nodeId)?.templateName || "unknown"}`);

type IGetMetadataOfNodesFirstParentOfTypeSignature = (
    allNodesHierarchy: INodesInfoAndHierarchy,
    currentNodeId: string,
    templateName: INode["templateName"],
    includeSelf?: boolean
) => INodeInfoAndHierarchy | null;

export const getMetadataOfNodesFirstParentOfType: IGetMetadataOfNodesFirstParentOfTypeSignature = (
    allNodesHierarchy,
    currentNodeId,
    templateName,
    includeSelf = true
) => {
    let nextNode = allNodesHierarchy?.[currentNodeId];
    if (!includeSelf) nextNode = allNodesHierarchy?.[nextNode.parentId]; // don't check the current node in this case
    while (nextNode) {
        if (nextNode.templateName == templateName) return nextNode;
        nextNode = allNodesHierarchy[nextNode.parentId]; // next parent
    }
    return null; // nothing found
};

export const getNodeFirstParentOfType = (state: IStoreState, currentNodeId: string, templateName: INode["templateName"], includeSelf: boolean = true): IBasicNode | null => {
    const parent = getMetadataOfNodesFirstParentOfType(getNodesHierarchy(state), currentNodeId, templateName, includeSelf);

    if (!parent) return null;

    return getBasicNodeById(state, parent?.id);
};

export const getCurrentNodeId = ({ currentNodeId }: IStoreState) => currentNodeId;

export const getCurrentMillNodeId = ({ currentMillNodeId }: IStoreState) => currentMillNodeId;

export const getCurrentLocationNodeId = ({ currentLocationNodeId }: IStoreState) => currentLocationNodeId;

export const getCurrentOrganizationNodeId = ({ currentOrganizationNodeId }: IStoreState): string => currentOrganizationNodeId;

export const getMillNode = (state: IStoreState, currentNodeId: string | null | undefined): IMillNode => currentNodeId ? getDetailedNodeById(state, extractMillFromNodeId(currentNodeId)) : null;
// eslint-disable-next-line @stylistic/max-len
export const getMillNodeMetadata = (state: IStoreState, currentNodeId: string | null | undefined): INodeInfoAndHierarchy => currentNodeId ? getNodeMetadataById(state, extractMillFromNodeId(currentNodeId)) : null;

export const getLocationName = (state: IStoreState, currentNodeId: string) => getNodeDisplayName(state, extractLocationFromNodeId(currentNodeId));

export const getNodesChildrenOfType = (state: IStoreState, currentNodeId: string, templateNames: INode["templateName"][]): Record<string, IBasicNode> => {
    const nodeHierarchy = getNodesHierarchy(state);
    const nodes = new Set(nodeHierarchy[currentNodeId]?.childrenIds);
    return getNodesFilteredByTemplate(state, nodes, templateNames, nodeHierarchy);
};

export const getNodesChildrenOfTypeRecursive = (
    state: IStoreState,
    currentNodeId: string,
    templateNames: INode["templateName"][],
    includeSelf: boolean = false
): Record<string, IBasicNode> => {
    const nodeHierarchy = getNodesHierarchy(state);
    const nodes = new Set(nodeHierarchy[currentNodeId]?.descendantIds);
    if (includeSelf) nodes.add(currentNodeId);

    return getNodesFilteredByTemplate(state, nodes, templateNames, nodeHierarchy);
};

const getNodesFilteredByTemplate = (state: IStoreState, nodes: Set<string>, templateNames: INode["templateName"][], nodeHierarchy: INodesInfoAndHierarchy): Record<string, IBasicNode> => {
    const templateNamesSet = new Set(templateNames || []);
    if (_.isEmpty(nodes)) return {};
    return _.chain(nodeHierarchy)
        .pickBy(({ id, templateName }) => nodes.has(id) && (!templateNames || templateNamesSet.has(templateName)))
        .map((_nodeInfo, id) => getBasicNodeById(state, id))
        .keyBy(({ id }) => id)
        .value();
};

export const getNodesByParentId = (state: IStoreState, parentId: string, neededPermissions?: IPermissionLevel[]): IBasicNodes => {
    const allNodes = getAllBasicNodes(state);
    return (
        neededPermissions
            ? _.pickBy(allNodes, (n) => n.parentId == parentId && UserModule.selectors.hasPermission(state, neededPermissions, extractLocationFromNodeId(n.id)))
            : _.pickBy(allNodes, (n) => n.parentId == parentId)
    );
};

export const getNodesByTemplateName = (state: IStoreState, templateName: ITemplate["name"]): IBasicNodes => {
    const basicNodes = getAllBasicNodes(state);

    const basicNodesForTemplate = _.pickBy(basicNodes, (node) => node?.templateName == templateName) as IBasicNodes;

    return basicNodesForTemplate;
};

export const getNodesHierarchical = memoize((state: IStoreState) => {
    const nodes: Record<string, INodeHierarchical> = _.mapValues(getAllBasicNodes(state), (node) => ({ ...node, childrenIds: [] as string[] }));
    _.forEach(nodes, (node) => {
        if (node.parentId) {
            nodes[node.parentId]?.childrenIds.push(node.id);
        }
    });
    return nodes;
});

export const makeGetWorstBranchStatus = (nodes: IBasicNodes) => memoize(({ nodeId, includeSelf }: { nodeId: string; includeSelf?: boolean; }): INodeStateStyle => includeSelf
    ? nodes[nodeId].branchStatuses.worstNodeStatusIncludingNode
    : nodes[nodeId].branchStatuses.worstChildNodeStatus);

export const makeGetBranchStatuses = (nodes: IBasicNodes) => memoize(({ nodeId }: { nodeId: string; }): IBranchStatuses => nodes[nodeId].branchStatuses);

/**
 * Returns the ids of the nodes in the same branch as the node currently selected
 */
export const getCurrentNodeBranchIds = memoize((state: IStoreState) => {
    const { currentNodeId } = state;
    const allIds: string[] = [currentNodeId];
    const nodes = getAllBasicNodes(state);
    let newParentId = currentNodeId;
    while (newParentId) {
        newParentId = nodes[newParentId].parentId;
        allIds.push(newParentId);
    }
    return _.reverse(allIds);
});

export const makeGetNodesHierarchicalTree = (neededPermissions?: IPermissionLevel[]) => (
    memoize(({ state, allowedTemplateNames }: { state: IStoreState; allowedTemplateNames: ITemplate["name"][]; }) => {
        const allNodes = _.pickBy(state.basicNodes, (node) => (
            (!allowedTemplateNames || allowedTemplateNames.includes(node.templateName)) &&
            (!neededPermissions || UserModule.selectors.hasPermission(state, neededPermissions, extractLocationFromNodeId(node.id)))));

        const nodes = _.mapValues(allNodes, (node) => ({
            id: node.id,
            name: NodesModule.selectors.getNodeDisplayName(state, node.id),
            parentId: node.parentId,
            type: null,
            childrenIds: [],
        } as INodeHierarchicalTree & { childrenIds: string[]; }));

        _.forEach(nodes, (node) => {
            if (node.parentId) {
                nodes[node.parentId]?.childrenIds.push(node.id);
            }
        });
        return nodes;
    })
);

/**
 * Get a simplified version of all nodes to cause less re-renders
 */
export const getAllBasicNodes = ({ basicNodes }: { basicNodes: IBasicNodes; }) => basicNodes;

export const getBasicNodeById = ({ basicNodes }: IStoreState, id: string) => basicNodes?.[id];

// This returns a dictionary of alpha-numeric values for ordering nodes or anything node-related, using the "order" field for each node level.
export const getNodesDisplayOrder = _.memoize((state: { basicNodes: IBasicNodes; }) => {
    const nodes = getAllBasicNodes(state);

    return _.reduce(nodes, (acc, node) => {
        const orders: (string | number)[] = [];
        const nodeIdSplit = _.split(node.id, "-");
        let path = "";

        nodeIdSplit.forEach((subNodeId) => {
            path += path ? "-" + subNodeId : subNodeId;
            const orderOrAlpha = nodes[path]?.order || subNodeId.toLocaleLowerCase();
            orders.push(orderOrAlpha);
        });

        acc[node.id] = orders.join("-");
        return acc;
    }, {} as Record<string, string>);
});

export const getNodesHierarchy = createSelector(
    (state: IStoreState) => getAllBasicNodes(state),
    (nodes): INodesInfoAndHierarchy => {
        const hierarchy = _.reduce(nodes, (acc, node) => {
            if (!acc[node.id]) acc[node.id] = { ...node, childrenIds: [], descendantIds: null };
            if (node.parentId && !nodes[node.parentId]) return acc;
            if (node.parentId && !acc[node.parentId]) acc[node.parentId] = { ...nodes[node.parentId], childrenIds: [], descendantIds: null };
            if (node.parentId) acc[node.parentId].childrenIds.push(node.id);
            return acc;
        }, {} as INodesInfoAndHierarchy);

        const injectDescendantsRecursive = (nodeId: string, acc: string[] = []): string[] => {
            if (hierarchy[nodeId].descendantIds) return hierarchy[nodeId].descendantIds; // don't re-evaluate nodes that we have already processed.
            const ids = hierarchy[nodeId].childrenIds;
            acc.push(...ids);
            _.forEach(ids, (subNodeId) => {
                const subIds = injectDescendantsRecursive(subNodeId);
                if (!_.isEmpty(subIds)) acc.push(...injectDescendantsRecursive(subNodeId));
            });
            hierarchy[nodeId].descendantIds = acc;
            return acc;
        };

        _.forEach(hierarchy, (node) => {
            injectDescendantsRecursive(node.id);
        });

        return hierarchy;
    },
    {
        memoizeOptions: { // Only recalculate this selector if the hiearchy might change
            equalityCheck: (a: IBasicNodes, b: IBasicNodes) => {
                if (a == b) return true; // basic equality
                if (_.size(a) != _.size(b)) return false; // size not matching

                try {
                    if (_.some({ ...a }, (n) => (
                        !b[n.id] || // Not the same nodes
                        n.parentId != b[n.id].parentId || // Hierarchy has changes
                        n.templateName != b[n.id].templateName || // Template has changes
                        n.name != b[n.id].name // Name has changes
                    ))) return false; // Not the same nodes or the hierarchy has changes
                } catch {
                    // If deconstruction is not enough for access, simply return false when access fails.
                    return false;
                }
                return true;
            },
        },
    }
);

const getOrderedChildrenOfType = (
    state: IStoreState,
    parentId: string | null,
    templateNames: INode["templateName"][]
): IBasicNode[] => {
    if (!parentId) return [];
    return _.orderBy(
        _.values(getNodesChildrenOfType(state, parentId, templateNames)),
        ({ id }) => getNodesDisplayOrder(state)[id]
    );
};

export const getMillsForCurrentLocation = memoize((state: IStoreState): IBasicNode[] => getOrderedChildrenOfType(
    state,
    state.currentLocationNodeId || extractLocationFromNodeId(state.currentNodeId),
    ["millCodec"]
));

export const getLocationsForCurrentOrg = memoize((state: IStoreState): IBasicNode[] => getOrderedChildrenOfType(
    state,
    state.currentOrganizationNodeId || extractOrganizationFromNodeId(state.currentNodeId),
    ["location"]
));

export const getAllMachinesForCurrentLocation = memoize(
    (state: IStoreState): IBasicNode[] => getOrderedChildrenOfType(
        state,
        state.currentLocationNodeId || extractLocationFromNodeId(state.currentNodeId),
        ["machineCodec"]
    )
);

export const getMachineNodesForMill = memoize(
    ({ state, millNodeId }: { state: IStoreState; millNodeId?: string; }): IBasicNode[] => millNodeId
        ? getOrderedChildrenOfType(state, millNodeId, ["machineCodec"])
        : getAllMachinesForCurrentLocation(state)
);

export const makeNodeNamesAvailable = () => memoize(({ state, nodeIds }: { state: IStoreState; nodeIds: string[]; }) => (
    _.map(nodeIds, (nodeId) => {
        const node = getBasicNodeById(state, nodeId);
        if (!node) return nodeId;
        return getNodeNamePath(state, node.id);
    })
));